// TemplateRenderer.jsx — Task 11. Renders an artifact's payload into checked-in React slot components.
//
// THE CONTRACT: the agent writes DATA (payload.template.slots — an ordered array of typed slots); this
// file maps each slot type → one checked-in component. No agent-authored code ever reaches the page.
// Unknown slot types render NOTHING (defensive skip + console.warn) so a forward-compat payload can
// never crash the viewer.
//
// The generic_report fallback (a templateless report, or a payload with no slots) renders
// payload.markdown through the shared AssistantMarkdown — the SAME isolation contract as
// ReportArtifact.jsx (no second markdown library; untrusted model text flows through one audited path).
import React from "react"
import AssistantMarkdown from "../AssistantMarkdown.jsx"
import KpiRow from "./slots/KpiRow.jsx"
import TrendLine from "./slots/TrendLine.jsx"
import RankedList from "./slots/RankedList.jsx"
import ComparisonGrid from "./slots/ComparisonGrid.jsx"
import NarrativeBlock from "./slots/NarrativeBlock.jsx"
import ActionList from "./slots/ActionList.jsx"

// type → component. Any type not here is skipped (defensive).
const SLOTS = {
  kpi_row: KpiRow,
  trend_line: TrendLine,
  ranked_list: RankedList,
  comparison_grid: ComparisonGrid,
  narrative_block: NarrativeBlock,
  action_list: ActionList,
}

function Slot({ slot }) {
  const type = slot && slot.type
  const Comp = SLOTS[type]
  if (!Comp) {
    if (typeof console !== "undefined" && console.warn) console.warn("[artifacts] unknown slot type:", type)
    return null
  }
  return <Comp {...slot} />
}

/**
 * Render an artifact payload. When it carries a registered template with slots, render the slot
 * sequence; otherwise fall back to the markdown body (generic_report) through AssistantMarkdown.
 * @param {{ payload?: object }} props — the FULL agent.report payload (title/markdown/template/…).
 */
export default function TemplateRenderer({ payload }) {
  const p = payload || {}
  const template = p.template
  const slots = template && Array.isArray(template.slots) ? template.slots : null

  if (slots && slots.length > 0) {
    return (
      <div className="afq-slots">
        {slots.map((slot, i) => <Slot slot={slot} key={i} />)}
      </div>
    )
  }

  // Generic fallback — the templateless report body (isolation contract preserved).
  const markdown = typeof p.markdown === "string" ? p.markdown : typeof p.body === "string" ? p.body : ""
  if (!markdown) return <div className="afq-empty-body">This report has no rendered content.</div>
  return (
    <div className="afq-card afq-generic" data-slot="generic_report">
      <AssistantMarkdown text={markdown} />
    </div>
  )
}
